import type React from 'react'

import type { OrderStatus } from '@/types/history'
import type { HistoryActionIconVariant } from './HistoryActionIcon'
import { HistoryIconBadge } from './HistoryIconRow'

export type HistoryItemType = 'buy' | 'airdrop' | 'send' | 'receive'

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

export interface HistoryItemProps {
  type?: HistoryItemType
  title?: string
  primaryAmount?: string
  secondaryAmount?: string
  timeLabel?: string
  icon?: React.ReactNode
  className?: string
  orderStatus?: OrderStatus | null
}

/**
 * 注文ステータスを日本語ラベルに変換
 */
const ORDER_STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string }> =
  {
    PENDING: {
      label: '決済待ち',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
    },
    ETH_SENT: {
      label: 'ETH送金中',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
    },
    ETH_RECEIVED: {
      label: 'ARX受取準備中',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
    },
    ARX_SENT: {
      label: 'ARX受取中',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
    },
    COMPLETED: {
      label: '完了',
      color: 'text-green-700',
      bgColor: 'bg-green-100',
    },
    EXPIRED: {
      label: '期限切れ',
      color: 'text-gray-700',
      bgColor: 'bg-gray-100',
    },
    FAILED: {
      label: '失敗',
      color: 'text-red-700',
      bgColor: 'bg-red-100',
    },
  }

const TYPE_CONFIG: Record<
  HistoryItemType,
  {
    title: string
    primaryAmount: string
    secondaryAmount?: string
    primaryColor: string
    secondaryColor?: string
    iconType: HistoryActionIconVariant
  }
> = {
  buy: {
    title: 'ARX 購入',
    primaryAmount: '+ 1,234 ARX',
    secondaryAmount: '- 0.021 ETH',
    primaryColor: 'text-green-500',
    secondaryColor: 'text-[#71717a]',
    iconType: 'buy',
  },
  airdrop: {
    title: 'ARX エアドロップ',
    primaryAmount: '+ 1,234 ARX',
    primaryColor: 'text-green-500',
    iconType: 'airdrop',
  },
  send: {
    title: 'ARX 送金',
    primaryAmount: '- 1,234 ARX',
    primaryColor: 'text-red-500',
    secondaryColor: 'text-[#71717a]',
    iconType: 'send',
  },
  receive: {
    title: 'ARX 受取',
    primaryAmount: '+ 1,234 ARX',
    primaryColor: 'text-green-500',
    iconType: 'receive',
  },
}

export const HistoryItem: React.FC<HistoryItemProps> = ({
  type = 'buy',
  title,
  primaryAmount,
  secondaryAmount,
  timeLabel,
  icon,
  className,
  orderStatus,
}) => {
  const config = TYPE_CONFIG[type]
  const resolvedTitle = title ?? config.title
  const resolvedPrimary = primaryAmount ?? config.primaryAmount
  const resolvedSecondary = secondaryAmount ?? config.secondaryAmount
  const resolvedTime = timeLabel ?? defaultTimeByType(type)
  const resolvedIcon = icon ?? <HistoryIconBadge type={config.iconType} />

  // 注文ステータスバッジ表示（購入履歴の場合）
  const statusConfig = orderStatus ? ORDER_STATUS_CONFIG[orderStatus] : null

  return (
    <div
      className={cx(
        'flex items-center gap-4 rounded-[16px] bg-[rgba(24,24,27,0.02)] p-4',
        className
      )}
    >
      <div className="relative h-10 w-10 shrink-0">{resolvedIcon}</div>
      <div className="flex flex-1 flex-col gap-1 overflow-hidden">
        <div className="flex items-center gap-2">
          <p className="m-0 text-sm font-medium leading-5 text-[#71717a]">{resolvedTitle}</p>
          {statusConfig && (
            <span
              className={cx(
                'px-2 py-0.5 text-xs font-medium rounded-full',
                statusConfig.color,
                statusConfig.bgColor
              )}
            >
              {statusConfig.label}
            </span>
          )}
        </div>
        {resolvedSecondary ? (
          <p
            className={cx(config.secondaryColor, 'break-words flex-1 text-xs whitespace-pre-line')}
            title={resolvedSecondary}
          >
            {resolvedSecondary}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1 items-end">
        <span className="m-0 text-sm font-medium leading-5 text-[#71717a] whitespace-nowrap">
          {resolvedTime}
        </span>
        <span className={cx('text-base font-semibold shrink-0', config.primaryColor)}>
          {resolvedPrimary}
        </span>
      </div>
    </div>
  )
}

const defaultTimeByType = (type: HistoryItemType): string => {
  switch (type) {
    case 'buy':
      return '1m'
    case 'airdrop':
      return '2h'
    case 'send':
      return '8h'
    case 'receive':
      return 'Aug 22'
    default:
      return ''
  }
}

export const HistoryList: React.FC<{ items?: HistoryItemType[]; className?: string }> = ({
  items = ['buy', 'airdrop', 'send', 'receive'],
  className,
}) => {
  const keyUsage = new Map<HistoryItemType, number>()

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {items.map((item) => {
        const occurrence = keyUsage.get(item) ?? 0
        keyUsage.set(item, occurrence + 1)
        const key = `${item}-${occurrence}`
        return <HistoryItem key={key} type={item} />
      })}
    </div>
  )
}

export default HistoryItem
